import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, CreditCard, CheckCircle2, Smartphone, Shield } from "lucide-react";
import bkashLogo from "@/assets/bkash-logo.webp";
import nagadLogo from "@/assets/nagad-logo.webp";
import rocketLogo from "@/assets/rocket-logo.webp";
import { toast } from "sonner";

type PaymentMethod = "bkash" | "nagad" | "rocket" | "card";

const paymentMethods: { id: PaymentMethod; name: string; logo?: string; bgClass: string }[] = [
  { id: "bkash", name: "bKash", logo: bkashLogo, bgClass: "from-[hsl(340,80%,45%)] to-[hsl(340,70%,55%)]" },
  { id: "nagad", name: "Nagad", logo: nagadLogo, bgClass: "from-[hsl(25,90%,50%)] to-[hsl(25,80%,60%)]" },
  { id: "rocket", name: "Rocket", logo: rocketLogo, bgClass: "from-[hsl(270,60%,40%)] to-[hsl(270,50%,50%)]" },
  { id: "card", name: "Card", bgClass: "from-[hsl(210,15%,30%)] to-[hsl(210,15%,40%)]" },
];

const PaymentPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const bn = language === "bn";

  const conversation = (location.state as any)?.conversation;
  const fee = conversation?.fee || 0;
  const dietitian = conversation ? (conversation.practitionerName || "") : "";

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const [gatewayStatus, setGatewayStatus] = useState<string | null>(null);

  useEffect(() => {
    const transactionId = new URLSearchParams(location.search).get("transaction_id");
    if (!transactionId || !user) return;

    let cancelled = false;
    let timer: number | undefined;
    const checkStatus = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return;
      const response = await fetch(`/api/payments/direct/status?transaction_id=${encodeURIComponent(transactionId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => ({}));
      if (cancelled) return;
      setGatewayStatus(result.status ?? null);
      if (result.status === "paid" || result.status === "completed") {
        setDone(true);
        return;
      }
      if (result.status === "failed" || result.status === "cancelled" || result.status === "refunded") {
        toast.error(bn ? "পেমেন্ট সম্পন্ন হয়নি" : "Payment was not completed");
        return;
      }
      timer = window.setTimeout(checkStatus, 2500);
    };
    checkStatus().catch(() => {
      if (!cancelled) toast.error(bn ? "পেমেন্টের অবস্থা দেখা যায়নি" : "Unable to check payment status");
    });
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [bn, location.search, user]);

  const handlePay = async () => {
    if (!user) return;
    setProcessing(true);
    try {
      if (!conversation?.id) throw new Error("Consultation not found");
      if (!paymentMethod) throw new Error(bn ? "একটি পেমেন্ট পদ্ধতি বেছে নিন" : "Choose a payment method");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error(bn ? "পেমেন্টের আগে সাইন ইন করুন" : "Please sign in before paying");

      const response = await fetch("/api/payments/direct/session", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ conversationId: conversation.id, method: paymentMethod, phone: phoneNumber }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error ?? (bn ? "পেমেন্ট শুরু করা যায়নি" : "Unable to start payment"));
      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
      } else {
        throw new Error(bn ? "পেমেন্ট প্রদানকারীর চেকআউট লিংক পাওয়া যায়নি" : "The payment provider did not return a checkout link");
      }
    } catch (error: any) {
      toast.error(error?.message ?? (bn ? "পেমেন্ট ব্যর্থ হয়েছে" : "Payment failed"));
      setProcessing(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center px-5">
        <motion.div className="glass rounded-3xl p-8 shadow-float text-center max-w-sm w-full" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, delay: 0.2 }}>
            <div className="w-20 h-20 rounded-full gradient-primary mx-auto flex items-center justify-center shadow-glow mb-5">
              <CheckCircle2 className="h-10 w-10 text-primary-foreground" />
            </div>
          </motion.div>
          <h2 className="text-2xl font-extrabold text-foreground mb-1">{bn ? "পেমেন্ট সম্পন্ন!" : "Payment Complete!"}</h2>
          <p className="text-sm text-muted-foreground mb-4">{bn ? "এখন আপনি মেসেজ ও ভিডিও কল করতে পারবেন" : "You can now message & video call"}</p>
          <button onClick={() => navigate("/patient-inbox")} className="w-full gradient-primary text-primary-foreground py-3.5 rounded-2xl font-bold text-sm shadow-glow">
            {bn ? "ইনবক্সে যান →" : "Go to Inbox →"}
          </button>
        </motion.div>
      </div>
    );
  }

  if (!conversation?.id) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center px-5">
        <div className="glass max-w-sm rounded-2xl p-6 text-center shadow-float">
          <h2 className="font-extrabold">{bn ? "পেমেন্ট সেশন পাওয়া যায়নি" : "Payment session not found"}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{bn ? "ইনবক্স থেকে একটি গ্রহণ করা পরামর্শ বেছে নিন।" : "Choose an accepted consultation from your inbox."}</p>
          <button onClick={() => navigate("/patient-inbox")} className="mt-4 w-full gradient-primary rounded-xl py-3 font-bold text-primary-foreground">{bn ? "ইনবক্সে যান" : "Go to Inbox"}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg pb-28">
      <div className="gradient-accent px-5 pt-12 pb-8 rounded-b-[2.5rem]">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-primary-foreground/15 flex items-center justify-center">
            <ArrowLeft className="h-5 w-5 text-primary-foreground" />
          </button>
          <h1 className="text-primary-foreground text-lg font-extrabold">{bn ? "পেমেন্ট করুন" : "Make Payment"}</h1>
        </div>
      </div>

      <div className="px-5 mt-4">
        <div className="glass rounded-2xl p-4 shadow-float flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center shadow-glow">
            <Smartphone className="h-6 w-6 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-sm">{dietitian}</p>
            <p className="text-xs text-muted-foreground">{bn ? "পরামর্শ ফি" : "Consultation Fee"}</p>
          </div>
          <p className="text-2xl font-extrabold text-primary">৳{fee}</p>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="h-4 w-4 text-primary" />
          <h2 className="font-bold text-sm">{bn ? "পেমেন্ট মেথড" : "Payment Method"}</h2>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          {paymentMethods.map((pm, i) => (
            <motion.button key={pm.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.08 }}
              onClick={() => setPaymentMethod(pm.id)}
              className={`rounded-2xl p-4 flex flex-col items-center gap-2.5 border-2 transition-all ${paymentMethod === pm.id ? "border-primary glass shadow-glow scale-[1.03]" : "border-transparent glass shadow-float"}`}>
              <div className={`w-12 h-12 rounded-xl ${pm.logo ? "bg-white" : ""} flex items-center justify-center ${pm.logo ? "shadow-md" : ""} overflow-hidden`}>
                {pm.logo ? (
                  <img src={pm.logo} alt={pm.name} className="w-9 h-9 object-contain" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-8 h-8">
                    <rect x="4" y="10" width="40" height="28" rx="4" fill="hsl(210,15%,50%)" />
                    <rect x="4" y="10" width="40" height="8" fill="hsl(210,15%,35%)" rx="4" />
                    <rect x="4" y="14" width="40" height="4" fill="hsl(210,15%,35%)" />
                    <rect x="8" y="26" width="12" height="3" rx="1.5" fill="hsl(45,90%,65%)" />
                    <rect x="8" y="31" width="8" height="2" rx="1" fill="hsl(0,0%,100%,0.4)" />
                    <circle cx="33" cy="30" r="4" fill="hsl(0,80%,55%)" opacity="0.85" />
                    <circle cx="38" cy="30" r="4" fill="hsl(35,90%,55%)" opacity="0.85" />
                  </svg>
                )}
              </div>
              <span className="text-xs font-extrabold">{pm.name}</span>
            </motion.button>
          ))}
        </div>

        {paymentMethod && paymentMethod !== "card" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="h-4 w-4 text-primary" />
              <label className="text-xs font-bold">{bn ? "মোবাইল নম্বর" : "Mobile Number"}</label>
            </div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">+880</span>
              <input type="tel" placeholder="1XXXXXXXXX" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full pl-16 pr-4 py-3.5 rounded-2xl bg-card text-foreground text-sm font-medium shadow-float border border-border outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </motion.div>
        )}

        {paymentMethod === "card" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-5 rounded-2xl border border-border bg-card/70 p-3 text-xs text-muted-foreground">
            {bn ? "কার্ডের তথ্য নিরাপদ পেমেন্ট প্রদানকারীর পেজে দিতে হবে। Sebaco কার্ড নম্বর বা CVV সংরক্ষণ করবে না।" : "You will enter card details on the secure provider page. Sebaco will never store your card number or CVV."}
          </motion.div>
        )}

        {gatewayStatus === "pending" && (
          <p className="mb-4 text-center text-xs font-semibold text-muted-foreground">
            {bn ? "পেমেন্ট যাচাই করা হচ্ছে..." : "Payment verification is still in progress..."}
          </p>
        )}

        <div className="flex items-center justify-center gap-1.5 mb-4">
          <Shield className="h-3.5 w-3.5 text-success" />
          <span className="text-[10px] text-muted-foreground">{bn ? "নিরাপদ পেমেন্ট" : "Secure Payment"}</span>
        </div>

        <button onClick={handlePay} disabled={!paymentMethod || processing || (paymentMethod !== "card" && !phoneNumber)}
          className="w-full gradient-primary text-primary-foreground py-4 rounded-2xl font-bold text-sm shadow-glow disabled:opacity-30 flex items-center justify-center gap-2">
          {processing ? (
            <div className="flex items-center gap-2">
              <motion.div className="h-5 w-5 border-2 border-primary-foreground border-t-transparent rounded-full" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }} />
              <span>{bn ? "প্রক্রিয়াকরণ..." : "Processing..."}</span>
            </div>
          ) : (
            <>{bn ? `পেমেন্ট করুন ৳${fee}` : `Pay ৳${fee}`}</>
          )}
        </button>
      </div>
    </div>
  );
};

export default PaymentPage;
